import { endOfDay, isWithinInterval, parseISO, startOfDay } from 'date-fns'

import { InvoiceStatus } from '@features/invoices/enums/invoiceStatus'

import { SalesReportDateSelection } from '../../../enums/salesReportDateSelection'
import { SalesReportSummary } from '../../../types/salesReportSummary'
import { GraphValueType } from '../enums/graphValueType'
import { SalesReportGraphBucket } from '../enums/salesReportGraphBucket'
import { SalesReportFormattedGraphData } from '../types/salesReportFormattedGraphData'
import { SalesReportGraphSummary } from '../types/salesReportGraphSummary'
import { getEmptyGraphValues } from './getEmptyGraphValues'
import { getSalesReportGraphTemplate } from './getSalesReportGraphTemplate'

interface FormatGraphData {
  bucket?: SalesReportGraphBucket
  graphData: SalesReportFormattedGraphData[]
  graphSummary?: SalesReportGraphSummary
}

interface FormatGraphDataProps {
  dateSelection: SalesReportDateSelection
  endDate?: string
  salesReport: SalesReportSummary[]
  startDate?: string
}

export const formatGraphData = ({
  dateSelection,
  endDate,
  salesReport,
  startDate,
}: FormatGraphDataProps): FormatGraphData => {
  if (!startDate || !endDate) {
    return { graphData: [] }
  }

  const { bucket, template } = getSalesReportGraphTemplate(parseISO(startDate), parseISO(endDate))
  const reportDateKey = dateSelection === SalesReportDateSelection.Payment ? 'paidDate' : 'issueDate'

  const graphData = template.map((graphBucket) => {
    const bucketReports = salesReport.filter((point) => {
      const reportDate = point[reportDateKey]

      return reportDate
        ? isWithinInterval(parseISO(reportDate), {
            start: startOfDay(graphBucket.startDate),
            end: endOfDay(graphBucket.endDate),
          })
        : false
    })

    const bucketValues = bucketReports.reduce((bucketResult, point) => {
      const isPaid = dateSelection === SalesReportDateSelection.Payment || point.status === InvoiceStatus.Paid
      const paidStatus = isPaid ? 'paid' : 'unpaid'

      bucketResult[GraphValueType.Gross][paidStatus] += point.grossTotal
      bucketResult[GraphValueType.Net][paidStatus] += point.netTotal

      return bucketResult
    }, getEmptyGraphValues())

    return { ...graphBucket, values: bucketValues }
  })

  const graphSummary = graphData.reduce((summaryResult, graphPoint) => {
    summaryResult[GraphValueType.Gross].paid += graphPoint.values.gross.paid
    summaryResult[GraphValueType.Gross].unpaid += graphPoint.values.gross.unpaid
    summaryResult[GraphValueType.Gross].total += graphPoint.values.gross.paid + graphPoint.values.gross.unpaid

    summaryResult[GraphValueType.Net].paid += graphPoint.values.net.paid
    summaryResult[GraphValueType.Net].unpaid += graphPoint.values.net.unpaid
    summaryResult[GraphValueType.Net].total += graphPoint.values.net.paid + graphPoint.values.net.unpaid

    return summaryResult
  }, getEmptyGraphValues())

  return {
    bucket,
    graphData,
    graphSummary,
  }
}
